import useErrorHandling, { ErrorTypes } from "../../../errors/useErrorHandling";
import usePreferences from "./usePreferences";
import { SERVICENOW } from "../../../constants/serviceNames";
import useServiceNowUser from "./useServiceNowUser";
import integrationsClient from "../clients/integrationsClient";
import serviceNowMapper from "../mappers/serviceNowMapper";
import useSWRInfinite from "swr/infinite";
import { useMemo } from "react";
import { SERVICENOW_INCIDENT_ROWS_PER_PAGE } from "../../../constants/generalConstants";
import { countAPIItems, reduceAPIItems } from "../../../utils/apiItemUtils";

const useServiceNowIncidents = (viewClosed) => {
    const { servicesInMaintenanceMode, preferencesAreLoading } =
        usePreferences();
    const {
        serviceNowUser,
        serviceNowUserIsLoading,
        errorMessage: serviceNowUserErrorMessage,
    } = useServiceNowUser();
    const { throwAsyncError } = useErrorHandling();

    const getCacheKey = (pageCount) => {
        if (
            preferencesAreLoading ||
            serviceNowUserIsLoading ||
            !serviceNowUser
        ) {
            return null;
        }
        return ["incidents", pageCount, viewClosed];
    };

    const {
        data: incidentsData,
        size: loadedPageCount,
        setSize: setPagesToLoad,
        isLoading: incidentsAreLoading,
        error: incidentsError,
    } = useSWRInfinite(
        getCacheKey,
        async (args) => {
            try {
                const params = {
                    limit: SERVICENOW_INCIDENT_ROWS_PER_PAGE,
                    offset: args[1] * SERVICENOW_INCIDENT_ROWS_PER_PAGE,
                    show_closed: args[2],
                };
                if (servicesInMaintenanceMode && serviceNowUser) {
                    if (
                        !servicesInMaintenanceMode.find(
                            (service) => service === SERVICENOW
                        )
                    ) {
                        const incidentData =
                            await integrationsClient.getIncidents(params, {
                                "sn-user-data-token": serviceNowUser,
                            });
                        return serviceNowMapper.mapAPIIncidentsToIncidents(
                            incidentData
                        );
                    }
                }
            } catch (error) {
                throwAsyncError(
                    ErrorTypes.ServiceNowIncidentsError,
                    "Unable to fetch incidents from ITSC",
                    error
                );
                throw new Error("Unable to fetch incidents from ITSC");
            }
        },
        { fallbackData: [], parallel: true, revalidateFirstPage: false }
    );

    const incidentList = useMemo(() => {
        return reduceAPIItems(incidentsData, []);
    }, [incidentsData]);

    const incidentCount = useMemo(() => {
        return countAPIItems(incidentsData);
    }, [incidentsData]);

    return {
        incidentList,
        loadedPageCount,
        setPagesToLoad,
        incidentsAreLoading,
        incidentCount,
        errorMessage: serviceNowUserErrorMessage || incidentsError?.message,
    };
};

export default useServiceNowIncidents;
